import React, { useEffect, useState } from "react";
import { Form, Button, message, notification } from "antd";
import InputCard from "../../../Components/Cards/InputCard";
import {
  getFleetAdmins,
  getFleetAdminVehicles,
  submitFuelDispensed,
} from "../../../API/FuelVendor/FuelDispensed";
import "./style.css";
import usePageAccess from "../../../Utlis/usePageAccess";

const getErrorMessage = (data, fallback) => {
  if (!data) return fallback;

  if (data.data && typeof data.data === "object" && !Array.isArray(data.data)) {
    const nestedFieldErrors = Object.entries(data.data)
      .filter(([, msg]) => typeof msg === "string")
      .map(([field, msg]) => `${field}: ${msg}`);

    if (nestedFieldErrors.length) return nestedFieldErrors.join(" | ");
  }

  if (data.message) return data.message;
  if (data.detail) return data.detail;

  const fieldErrors = Object.values(data)
    .flat()
    .filter((msg) => typeof msg === "string");

  return fieldErrors.length ? fieldErrors.join(" ") : fallback;
};

const FuelDispensed = () => {
  const { canEdit } = usePageAccess("fuel-dispensed");
  const [form] = Form.useForm();
  const [fleetAdminOptions, setFleetAdminOptions] = useState([]);
  const [selectedFuelTypeId, setSelectedFuelTypeId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [vehicleOptions, setVehicleOptions] = useState([]);
  const [vehiclesById, setVehiclesById] = useState({});
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const watchedFleetAdmin = Form.useWatch("fleetAdmin", form);
  const watchedVehicle = Form.useWatch("vehicle", form);

  useEffect(() => {
    const fetchFleetAdmins = async () => {
      try {
        const res = await getFleetAdmins();
        const list = res?.data?.data;

        if (Math.floor(res?.status / 100) === 2 && Array.isArray(list)) {
          setFleetAdminOptions(
            list.map((admin) => ({ label: admin.name, value: admin.id })),
          );
        } else {
          message.error(
            getErrorMessage(res?.data, "Failed to load fleet admins."),
          );
        }
      } catch (err) {
        message.error(
          getErrorMessage(err?.response?.data, "Failed to load fleet admins."),
        );
      }
    };


    fetchFleetAdmins();
  }, []);

  useEffect(() => {
    if (!watchedFleetAdmin) {
      setVehicleOptions([]);
      setVehiclesById({});
      return;
    }

    const fetchVehicles = async () => {
      setLoadingVehicles(true);
      try {
        const res = await getFleetAdminVehicles(watchedFleetAdmin);
        const list = res?.data?.data;

        if (Math.floor(res?.status / 100) === 2 && Array.isArray(list)) {
          setVehicleOptions(
            list.map((v) => ({
              label: `${v.vehicle_name} (${v.license_plate})`,
              value: v.id,
            })),
          );
          setVehiclesById(
            list.reduce((acc, v) => {
              acc[v.id] = v;
              return acc;
            }, {}),
          );
        } else {
          setVehicleOptions([]);
          setVehiclesById({});
          message.error(
            getErrorMessage(res?.data, "Failed to load vehicles."),
          );
        }
      } catch (err) {
        setVehicleOptions([]);
        setVehiclesById({});
        message.error(
          getErrorMessage(err?.response?.data, "Failed to load vehicles."),
        );
      } finally {
        setLoadingVehicles(false);
      }
    };

    fetchVehicles();
    form.setFieldsValue({
      vehicle: undefined,
      vehicleMake: "",
      plateNumber: "",
      driverName: "",
      licenseNo: "",
      fuelTypeName: "",
    });
    setSelectedFuelTypeId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedFleetAdmin]);

  useEffect(() => {
    if (!watchedVehicle) return;

    const vehicle = vehiclesById[watchedVehicle];
    if (!vehicle) return;

    form.setFieldsValue({
      vehicleMake: vehicle.vehicle_make || "",
      plateNumber: vehicle.license_plate || "",
      driverName: vehicle.driver_name || "",
      licenseNo: vehicle.driver_license || "",
      fuelTypeName: vehicle.fuel_type_name || "",
    });
    setSelectedFuelTypeId(vehicle.fuel_type ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedVehicle]);

  const vehicleInformationFields = [
    {
      name: "fleetAdmin",
      label: "Fleet Admin",
      type: "select",
      placeholder: "Select Fleet Admin",
      options: fleetAdminOptions,
      rules: [{ required: true, message: "Fleet Admin is required" }],
      fullWidth: true,
    },
    {
      name: "vehicle",
      label: "Vehicle",
      type: "select",
      placeholder: watchedFleetAdmin
        ? "Select Vehicle"
        : "Select Fleet Admin first",
      options: vehicleOptions,
      disabled: !watchedFleetAdmin || loadingVehicles,
      fullWidth: true,
    },
    {
      name: "vehicleMake",
      label: "Vehicle Make",
      type: "text",
      placeholder: "Auto-filled from vehicle",
      disabled: true,
    },
    {
      name: "plateNumber",
      label: "Plate Number",
      type: "text",
      placeholder: "Auto-filled from vehicle",
      disabled: true,
    },
    {
      name: "driverName",
      label: "Driver Name",
      type: "text",
      placeholder: "Auto-filled from vehicle",
      disabled: true,
    },
    {
      name: "licenseNo",
      label: "Driver's License No.",
      type: "text",
      placeholder: "Auto-filled from vehicle",
      disabled: true,
    },
  ];

  const fuelDispensedFields = [
    {
      name: "fuelTypeName",
      label: "Fuel Type",
      type: "text",
      placeholder: "Auto-filled from vehicle",
      disabled: true,
    },
    {
      name: "volumeDispensed",
      label: "Volume Dispensed",
      type: "number",
      placeholder: "0.00",
      addonAfter: "L",
      rules: [{ required: true, message: "Volume is required" }],
    },
    {
      name: "totalCost",
      label: "Total Transaction Cost",
      type: "number",
      placeholder: "0.00",
      addonAfter: "$",
      rules: [{ required: true, message: "Total cost is required" }],
    },
    {
      name: "stationLicense",
      label: "Station License",
      type: "text",
      placeholder: "Enter Station License",
      rules: [{ required: true, message: "Station License is required" }],
    },
  ];

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (!selectedFuelTypeId) {
        message.error("The selected vehicle has no fuel type assigned.");
        return;
      }

      const payload = {
        fleeiq_admin: values.fleetAdmin,
        vehilce_make: values.vehicleMake,
        plate_no: values.plateNumber,
        driver_name: values.driverName,
        license_no: values.licenseNo,
        fuel_type_id: selectedFuelTypeId,
        volume: values.volumeDispensed,
        transaction_cost: values.totalCost,
        station_license: values.stationLicense,
      };

      setSubmitting(true);
      const res = await submitFuelDispensed(payload);
      const data = res?.data?.data;

      if (Math.floor(res?.status / 100) === 2) {
        if (data?.status === "denied") {
          notification.warning({
            message: "Transaction Denied",
            description:
              data?.denial_message ||
              data?.denial_reason ||
              "This transaction was denied.",
            placement: "topRight",
          });
        } else {
          message.success(
            res?.data?.message || "Fuel Dispensed submitted successfully",
          );
        }
        form.resetFields();
      } else {
        notification.error({
          message: "Submission Failed",
          description: getErrorMessage(
            res?.data,
            "Failed to submit fuel dispensed transaction.",
          ),
          placement: "topRight",
        });
      }
    } catch (err) {
      if (err?.errorFields) return;
      notification.error({
        message: "Submission Failed",
        description: getErrorMessage(
          err?.response?.data,
          "Failed to submit fuel dispensed transaction.",
        ),
        placement: "topRight",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fv-dispensed">
      {/* Header */}
      <div className="fv-dispensed__page-header">
        <span className="fv-dispensed__page-title">Fuel Dispensed</span>
      </div>

      <Form form={form} layout="vertical">
        {/* Vehicle Info Card */}
        <InputCard
          title="Vehicle Information"
          fields={vehicleInformationFields}
        />

        <InputCard title="Fuel Dispensed" fields={fuelDispensedFields} />

        {canEdit && (
          <div className="btns-row">
            <Button
              onClick={() => form.resetFields()}
              className="fv-dispensed__btn-cancel"
            >
              Cancel
            </Button>

            <Button
              type="primary"
              onClick={handleSubmit}
              className="save-btn"
              loading={submitting}
              disabled={submitting}
            >
              Submit
            </Button>
          </div>
        )}
      </Form>
    </div>
  );
};

export default FuelDispensed;
